import type { GameraDomain } from '.'

export type LeagueParam = Lowercase<GameraDomain>

export type FcSubDomain = 'eu5' | 'epl' | 'lal' | 'bun' | 'ser' | 'lig'

export const fcSubdomainsToGameraDomain = (
  d: Uppercase<FcSubDomain> | GameraDomain,
): GameraDomain => {
  switch (d) {
    case 'EU5':
    case 'EPL':
    case 'PL':
    case 'LAL':
    case 'BUN':
    case 'SER':
    case 'LIG':
      return 'FC'
    default:
      return d
  }
}

export const isLeague = (param: string | undefined | null) => {
  return [
    'nfl',
    'nba',
    'mlb',
    'nhl',
    'cfb',
    'pga',
    'fc',
    'epl',
    'wnba',
  ].includes(param?.toLowerCase() ?? '')
}

export const leagueToDomain = (param: LeagueParam): GameraDomain => {
  return param.toUpperCase() as GameraDomain
}

export const domainToLeague = (param: GameraDomain): LeagueParam => {
  return param.toLowerCase() as LeagueParam
}

export const domainToLeagueAlt = (param: GameraDomain | 'epl'): string => {
  const lowercase = param.toLowerCase()
  if (lowercase === 'epl') return 'PL'
  if (lowercase === 'lal') return 'LaLiga'
  if (lowercase === 'ser') return 'Serie A'
  if (lowercase === 'bun') return 'Bundesliga'
  if (lowercase === 'lig') return 'Ligue 1'
  if (lowercase === 'eu5') return 'Europe Top 5'
  return param.toUpperCase()
}

export const domainToLeagueParam = (param: GameraDomain): string => {
  const lowercase = param.toLowerCase()
  if (lowercase === 'epl') return 'pl'
  if (lowercase === 'lal') return 'laliga'
  if (lowercase === 'ser') return 'seriea'
  if (lowercase === 'bun') return 'bundesliga'
  if (lowercase === 'lig') return 'ligue1'
  return lowercase as LeagueParam
}

export const normalizeLeagueParam = (param: string): string => {
  if (!param) return ''
  const lowercase = param.toLowerCase()
  if (lowercase === 'pl') return 'epl'
  if (lowercase === 'laliga') return 'lal'
  if (lowercase === 'seriea') return 'ser'
  if (lowercase === 'bundesliga') return 'bun'
  if (lowercase === 'ligue1') return 'lig'
  return lowercase as LeagueParam
}

export const leagueOrder = (): LeagueParam[] => {
  return domainOrder().map(domainToLeague)
}

export const domainOrder = (): GameraDomain[] => {
  switch (new Date().getMonth() + 1) {
    case 1:
      // Jan: NFL, CFB, NBA, FC, NHL, MLB, WNBA, PGA, Money
      return ['NFL', 'CFB', 'NBA', 'FC', 'NHL', 'MLB', 'WNBA', 'PGA']
    case 2:
      // Feb: NFL, NBA, FC, NHL, MLB, CFB, WNBA, PGA, Money
      return ['NFL', 'NBA', 'FC', 'NHL', 'MLB', 'CFB', 'WNBA', 'PGA']
    case 3:
      // Mar: NBA, FC, NHL, MLB, NFL, CFB, WNBA, PGA, Money
      return ['NBA', 'FC', 'NHL', 'MLB', 'NFL', 'CFB', 'WNBA', 'PGA']
    case 4:
      // Apr: NBA, NHL, FC, MLB, NFL, CFB, WNBA, PGA, Money
      return ['NBA', 'NHL', 'FC', 'MLB', 'NFL', 'CFB', 'WNBA', 'PGA']
    case 5:
      // May: NBA, NHL, FC, MLB, WNBA, NFL, CFB, PGA, Money
      return ['NBA', 'NHL', 'FC', 'MLB', 'WNBA', 'NFL', 'CFB', 'PGA']
    case 6:
      // Jun: NBA, NHL, MLB, WNBA, FC, NFL, CFB, PGA, Money
      return ['NBA', 'NHL', 'MLB', 'WNBA', 'FC', 'NFL', 'CFB', 'PGA']
    case 7:
      // Jul: MLB, WNBA, NBA, NHL, FC, NFL, CFB, PGA, Money
      return ['MLB', 'WNBA', 'NBA', 'NHL', 'FC', 'NFL', 'CFB', 'PGA']
    case 8:
      // Aug: CFB, MLB, WNBA, FC, NBA, NHL, NFL, PGA, Money
      return ['CFB', 'MLB', 'WNBA', 'FC', 'NBA', 'NHL', 'NFL', 'PGA']
    case 9:
      // Sep: NFL, CFB, MLB, WNBA, FC, NBA, NHL, PGA, Money
      return ['NFL', 'CFB', 'MLB', 'WNBA', 'FC', 'NBA', 'NHL', 'PGA']
    case 10:
      // Oct: MLB, WNBA, NBA, NFL, CFB, FC, NHL, PGA, Money
      return ['MLB', 'WNBA', 'NBA', 'NFL', 'CFB', 'FC', 'NHL', 'PGA']
    case 11:
      // Nov: NBA, NFL, CFB, FC, NHL, MLB, WNBA, PGA, Money
      return ['NBA', 'NFL', 'CFB', 'FC', 'NHL', 'MLB', 'WNBA', 'PGA']
    case 12:
      // Dec: NBA, NFL, CFB, FC, NHL, MLB, WNBA, PGA, Money
      return ['NBA', 'NFL', 'CFB', 'FC', 'NHL', 'MLB', 'WNBA', 'PGA']
    default:
      return ['NBA', 'NFL', 'CFB', 'FC', 'NHL', 'MLB', 'WNBA', 'PGA']
  }
}

export const leagueOrderAlt = (): LeagueParam[] => {
  return domainOrderAlt().map(domainToLeague)
}

export const domainOrderAlt = (): GameraDomain[] => {
  switch (new Date().getMonth() + 1) {
    case 1:
      // Jan: NFL, CFB, NBA, FC, NHL, MLB, WNBA, PGA, Money
      return ['NFL', 'CFB', 'NBA', 'FC', 'NHL', 'MLB', 'WNBA', 'PGA']
    case 2:
      // Feb: NBA, FC, NHL, NFL, CFB, MLB, WNBA, PGA, Money
      return ['NBA', 'FC', 'NHL', 'NFL', 'CFB', 'MLB', 'WNBA', 'PGA']
    case 3:
      // Mar: NBA, FC, NHL, MLB, NFL, CFB, WNBA, PGA, Money
      return ['NBA', 'FC', 'NHL', 'MLB', 'NFL', 'CFB', 'WNBA', 'PGA']
    case 4:
      // Apr: NBA, FC, NHL, MLB, NFL, CFB, WNBA, PGA, Money
      return ['NBA', 'FC', 'NHL', 'MLB', 'NFL', 'CFB', 'WNBA', 'PGA']
    case 5:
      // May: FC, MLB, WNBA, NBA, NHL, NFL, CFB, PGA, Money
      return ['FC', 'MLB', 'WNBA', 'NBA', 'NHL', 'NFL', 'CFB', 'PGA']
    case 6:
      // Jun: MLB, WNBA, FC, NBA, NHL, NFL, CFB, PGA, Money
      return ['MLB', 'WNBA', 'FC', 'NBA', 'NHL', 'NFL', 'CFB', 'PGA']
    case 7:
      // Jul: MLB, WNBA, FC, NBA, NHL, NFL, CFB, PGA, Money
      return ['MLB', 'WNBA', 'FC', 'NBA', 'NHL', 'NFL', 'CFB', 'PGA']
    case 8:
      // Aug: MLB, CFB, WNBA, FC, NBA, NHL, NFL, PGA, Money
      return ['MLB', 'CFB', 'WNBA', 'FC', 'NBA', 'NHL', 'NFL', 'PGA']
    case 9:
      // Sep: MLB, NFL, CFB, WNBA, FC, NBA, NHL, PGA, Money
      return ['MLB', 'NFL', 'CFB', 'WNBA', 'FC', 'NBA', 'NHL', 'PGA']
    case 10:
      // Oct: NFL, CFB, NBA, FC, NHL, MLB, WNBA, PGA, Money
      return ['NFL', 'CFB', 'NBA', 'FC', 'NHL', 'MLB', 'WNBA', 'PGA']
    case 11:
      // Nov: NFL, CFB, NBA, FC, NHL, MLB, WNBA, PGA, Money
      return ['NFL', 'CFB', 'NBA', 'FC', 'NHL', 'MLB', 'WNBA', 'PGA']
    case 12:
      // Dec: NFL, CFB, NBA, FC, NHL, MLB, WNBA, PGA, Money
      return ['NFL', 'CFB', 'NBA', 'FC', 'NHL', 'MLB', 'WNBA', 'PGA']
    default:
      return ['NFL', 'CFB', 'NBA', 'FC', 'NHL', 'MLB', 'WNBA', 'PGA']
  }
}
